import type {
  ConcreteMaterialSettings,
  ConcreteQuality,
  ConcreteSourceMode,
} from '../materials/authoring/ConcreteMaterial';
import { SCANNED_CONCRETE_ATTRIBUTION } from '../materials/authoring/ConcreteMaterial';
import {
  CONCRETE_PRESETS,
  CONCRETE_QUALITY_OPTIONS,
  CONCRETE_SHOWCASES,
  CONCRETE_STATE_PROOF,
  type ConcretePreset,
  type ConcreteShowcaseId,
} from './concreteMaterialLabData';

interface ConcreteMaterialLabProps {
  settings: ConcreteMaterialSettings;
  preset: ConcretePreset;
  activeShowcase: ConcreteShowcaseId;
  tourActive: boolean;
  tourIndex: number;
  tourCount: number;
  telemetry: {
    fps: number;
    calls: number;
    triangles: number;
    textures: number;
    pixelRatio: number;
    dynamicChunks: number;
  };
  onPreset: (preset: ConcretePreset) => void;
  onChange: (patch: Partial<ConcreteMaterialSettings>) => void;
  onQuality: (quality: ConcreteQuality) => void;
  onSource: (source: ConcreteSourceMode) => void;
  onShowcase: (id: ConcreteShowcaseId) => void;
  onStartTour: () => void;
  onClose: () => void;
}

function RangeControl({
  label,
  value,
  min,
  max,
  step,
  suffix,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  suffix?: string;
  onChange: (value: number) => void;
}) {
  return (
    <label className="concrete-lab-range">
      <span>
        {label}
        <strong>
          {value.toFixed(step < 0.1 ? 2 : 1)}
          {suffix ?? ''}
        </strong>
      </span>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(event) => onChange(Number(event.target.value))}
      />
    </label>
  );
}

export default function ConcreteMaterialLab({
  settings,
  preset,
  activeShowcase,
  tourActive,
  tourIndex,
  tourCount,
  telemetry,
  onPreset,
  onChange,
  onQuality,
  onSource,
  onShowcase,
  onStartTour,
  onClose,
}: ConcreteMaterialLabProps) {
  const active = CONCRETE_SHOWCASES.find((item) => item.id === activeShowcase) ?? CONCRETE_SHOWCASES[0];
  const source = settings.source ?? 'procedural';
  const scannedActive = source === 'scanned-pbr';

  return (
    <aside className="concrete-lab" data-testid="concrete-lab" aria-label="Concrete material lab">
      <div className="concrete-lab-header">
          <div>
            <div className="concrete-lab-kicker">PHASE 1 LOOKDEV</div>
            <h2>Concrete Material Lab</h2>
            <span className="concrete-lab-mobile-hint">Swipe for controls · close to inspect the wall</span>
          </div>
        <button type="button" className="concrete-lab-close" onClick={onClose} aria-label="Close concrete lab">
          ×
        </button>
      </div>

      <div className="concrete-lab-proof" data-testid="concrete-lab-proof">
        <span className="concrete-lab-proof-label">PROVING</span>
        <strong>{active.label}</strong>
        <p>{active.proof}</p>
      </div>

      <section className="concrete-lab-section" data-testid="concrete-source">
        <div className="concrete-lab-section-title">Material source (A/B)</div>
        <div className="concrete-lab-source" role="group" aria-label="Concrete material source">
          <button
            type="button"
            className={`concrete-lab-source-btn${source === 'procedural' ? ' is-active' : ''}`}
            onClick={() => onSource('procedural')}
            data-testid="concrete-source-procedural"
          >
            Current procedural
          </button>
          <button
            type="button"
            className={`concrete-lab-source-btn${scannedActive ? ' is-active' : ''}`}
            onClick={() => onSource('scanned-pbr')}
            data-testid="concrete-source-scanned-pbr"
          >
            Scanned PBR
          </button>
        </div>
        <p className="concrete-lab-source-hint">
          Same wall, camera, and lighting — switch only the concrete surface model.
        </p>
        {scannedActive && (
          <div className="concrete-lab-attribution" data-testid="concrete-source-attribution">
            <span className="concrete-lab-cc0">CC0</span>
            <div>
              <strong>ambientCG scans</strong>
              <p>
                Exterior:{' '}
                <a href={SCANNED_CONCRETE_ATTRIBUTION.exteriorUrl} target="_blank" rel="noreferrer">
                  {SCANNED_CONCRETE_ATTRIBUTION.exteriorId}
                </a>
                {' · '}
                Interior:{' '}
                <a href={SCANNED_CONCRETE_ATTRIBUTION.interiorUrl} target="_blank" rel="noreferrer">
                  {SCANNED_CONCRETE_ATTRIBUTION.interiorId}
                </a>
                {' · '}
                {SCANNED_CONCRETE_ATTRIBUTION.resolution}
              </p>
            </div>
          </div>
        )}
      </section>

      <div className="concrete-lab-tour">
        <div>
          <span>Guided proof tour</span>
          {tourActive && <strong>{tourIndex + 1}/{tourCount}</strong>}
        </div>
        <button type="button" className="concrete-lab-tour-btn" onClick={onStartTour} data-testid="concrete-tour">
          {tourActive ? 'Restart tour' : 'Run all examples'}
        </button>
      </div>

      <section className="concrete-lab-section">
        <div className="concrete-lab-section-title">Surface states</div>
        <div className="concrete-lab-presets">
          {CONCRETE_PRESETS.map((item) => (
            <button
              key={item.id}
              type="button"
              className={`concrete-lab-chip${preset === item.id ? ' is-active' : ''}`}
              onClick={() => onPreset(item.id)}
              data-testid={`concrete-preset-${item.id}`}
              title={item.description}
            >
              {item.label}
            </button>
          ))}
        </div>
        <p className="concrete-lab-state-proof" data-testid="concrete-state-proof">
          {CONCRETE_STATE_PROOF[preset]}
        </p>
      </section>

      <div className="concrete-lab-telemetry" data-testid="concrete-telemetry">
        <div><span>FPS</span><strong>{telemetry.fps}</strong></div>
        <div><span>Draws</span><strong>{telemetry.calls}</strong></div>
        <div><span>Triangles</span><strong>{telemetry.triangles.toLocaleString()}</strong></div>
        <div><span>Chunks</span><strong>{telemetry.dynamicChunks}</strong></div>
        <div><span>DPR</span><strong>{telemetry.pixelRatio.toFixed(2)}</strong></div>
        <div><span>Textures</span><strong>{telemetry.textures}</strong></div>
      </div>

      <section className="concrete-lab-section">
        <div className="concrete-lab-section-title">Material controls</div>
        <RangeControl
          label="Texture scale"
          value={settings.textureScale}
          min={0.2}
          max={2.4}
          step={0.05}
          suffix=" m"
          onChange={(value) => onChange({ textureScale: value })}
        />
        <RangeControl
          label="Roughness"
          value={settings.roughness}
          min={0.35}
          max={1}
          step={0.01}
          onChange={(value) => onChange({ roughness: value })}
        />
        <RangeControl
          label="Micro normal"
          value={settings.normalStrength}
          min={0}
          max={0.8}
          step={0.05}
          onChange={(value) => onChange({ normalStrength: value })}
        />
        <RangeControl
          label="Weathering mottling"
          value={settings.macroVariation}
          min={0}
          max={1}
          step={0.01}
          onChange={(value) => onChange({ macroVariation: value })}
        />
        <RangeControl
          label="Moisture (darker + shinier)"
          value={settings.moisture}
          min={0}
          max={1}
          step={0.01}
          onChange={(value) => onChange({ moisture: value })}
        />
        <RangeControl
          label="Fresh aggregate"
          value={settings.interiorAmount}
          min={0}
          max={1}
          step={0.01}
          onChange={(value) => onChange({ interiorAmount: value })}
        />
      </section>

      <section className="concrete-lab-section">
        <div className="concrete-lab-section-title">Quality budget</div>
        <div className="concrete-lab-quality" role="group" aria-label="Concrete quality tier">
          {CONCRETE_QUALITY_OPTIONS.map((quality) => (
            <button
              key={quality}
              type="button"
              className={`concrete-lab-quality-btn${settings.quality === quality ? ' is-active' : ''}`}
              onClick={() => onQuality(quality)}
              data-testid={`concrete-quality-${quality}`}
            >
              {quality}
            </button>
          ))}
        </div>
      </section>

      <section className="concrete-lab-section concrete-lab-showcases">
        <div className="concrete-lab-section-title">Proof scenes</div>
        <div className="concrete-lab-showcase-grid">
          {CONCRETE_SHOWCASES.map((item) => (
            <button
              key={item.id}
              type="button"
              className={`concrete-lab-showcase${activeShowcase === item.id ? ' is-active' : ''}`}
              onClick={() => onShowcase(item.id)}
              data-testid={`concrete-showcase-${item.id}`}
            >
              <strong>{item.label}</strong>
              <span>{item.proof}</span>
            </button>
          ))}
        </div>
      </section>

      <div className="concrete-lab-note">
        Phase 1 boundary: one shared concrete system, no per-chunk material clones. Procedural noise and
        scanned PBR share the same rest-position mapping so chunks never swim. Cracks, rebar, and advanced
        anti-tiling come later.
      </div>
    </aside>
  );
}
